import * as React from 'react'
import styled from 'styled-components'
import { StoreProps } from '../../../../services/stores/casting.store'
import { formatDurationFromSecs } from '../../../../services/utils/utils.service'
import InputRangeComponent from '../../../shared/input-range.component'
import { I18nService } from '../../../../services/utils/i18n.service'
import { OfferedOptionBase } from '../../../../services/json-schemas/base/offered_option'

export const SelectableIcons = styled.ul`
  font-size: 1.14286em;
  margin-left: -0.3125em;
  margin-right: -0.3125em;
  margin-bottom: 0.75em;
  padding-left: 1.5em;

  @media (max-width: ${props => props.theme.breakpoints.m}px) {
    text-align: center;
    margin-top: .625em;
  }
`

export const SelectableItem = styled.li`
  text-align: center;
  vertical-align: top;
  display: inline-block;
  margin: 0 0.3125em;
  position: relative;
  
  input[type="checkbox"] {
    position: absolute;
    visibility: hidden;
    opacity: 0;
  }

  label {
    display: block;
    transition: all 0.3s ease-in-out;
    max-width: 4.375em;
  }

  @media (max-width: ${props => props.theme.breakpoints.m}px) {
    margin-bottom: 0.3125em;
  }
`

export const SelectableIconContainer = styled.div<{ $selected: boolean }>`
  width: 4.375em;
  height: 4.375em;
  background: ${ props => props.theme.colors[props.$selected ? 'clr_1' : 'clr_0_lightest'] };
  text-align: center;
  border-radius: 0.25rem;
  display: table-cell;
  vertical-align: middle;
  transition: all 0.3s ease-in-out;
  fill: ${ props => props.$selected ? 'white' : props.theme.colors.clr_0 };
  overflow: hidden;
  ${ props => props.$selected && 'color: white;' }

  .iconSVG {
    width: 2.375em;
    height: 2.375em;
    display: inline-block;
    vertical-align: middle;
  }
`

export const SelectableIconText = styled.span<{ $selected: boolean }>`
  font-size: 0.7em;
  display: inline-block;
  line-height: 1.2em;
  margin-top: 0.3125em;
  transition: all 0.3s ease-in-out;
  hyphens: auto;
  text-align: center;
  ${ props => props.$selected && `font-weight:700; color: ${ props.theme.colors.clr_0_dark };` }
`

const SelectableIconDescription = styled.div`
  background: ${ props => props.theme.colors.clr_1 };
  text-align: center;
  padding: 13px;
  font-size: 0.875em;
  color: white;
  border-radius: 0.25rem;
  margin-top: 1.4rem;
`

export const RangeBox = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  padding: 0.5em;
  pointer-events: none;

  .noUi-container {
    pointer-events: auto;
  }

  .noUi-handle {
    background-color: white !important;
  }
`

export const RangeDuration = styled.div`
  color: #FFF;
  margin: 0.125em 0;
`

type SelectedOption = StoreProps['data']['selectedOptions'][0]
type OfferedOption = StoreProps['data']['offeredService']['offered_options'][0]
type OfferedOptionOfferedService = (StoreProps['data']['offeredService']['offered_options'][0] & { option_type: 'product_catalogue' })['offered_option_offered_services'][0]

const i18nScoped = new I18nService(require('./casting-options-picker.component.yml'))

export default function CastingOptionsPickerComponent ({ store, data: { selectedOptions, offeredService: os } }: StoreProps) {
  const [focusedDescription, setFocusedDescription] = React.useState<string>(null)
  const [currentOpenCatalogueId, setCurrentOpenCatalogueId] = React.useState<OfferedOptionBase['id']>(null)

  const onOfferedOptionClicked = React.useCallback((offeredOption: OfferedOption, checked: boolean) => {
    if (offeredOption.option_type === 'product_catalogue') {
      setCurrentOpenCatalogueId(checked ? offeredOption.id : null)
    } else if (!store.isOfferedOptionMandatory(offeredOption)) {
      const newSelectedOptions = checked
        ? selectedOptions.concat({ offered_option_id: offeredOption.id, selected_value: offeredOption.value_min, offered_service_id: null })
        : selectedOptions.filter(_ => _.offered_option_id !== offeredOption.id)

      store.set({ selectedOptions: newSelectedOptions })
    }
  }, [store, selectedOptions])

  const onOfferedOptionOfferedServiceClicked = React.useCallback((
    offeredOption: OfferedOption & { option_type: 'product_catalogue' },
    offeredOptionOfferedService: OfferedOptionOfferedService,
    selectedOption: SelectedOption
  ) => {
    if (offeredOptionOfferedService.required_status !== 'no_required') return

    store.set({
      selectedOptions: selectedOptions
        .filter(_ => _ !== selectedOption)
        .concat(selectedOption ? [] : { offered_option_id: offeredOption.id, selected_value: offeredOptionOfferedService.offered_service.quantity_min, offered_service_id: offeredOptionOfferedService.offered_service.id })
    })
  }, [store, selectedOptions])

  const handleSliderChange = React.useCallback((selectedOption: SelectedOption, selectedDuration: number) => {
    selectedDuration = Math.round(selectedDuration)
    store.set({ selectedOptions: selectedOptions.map(oo => oo === selectedOption ? { ...oo, selected_value: selectedDuration } : oo) })
  }, [store, selectedOptions])

  const renderOfferedOptionIcon = React.useCallback((offeredOption: OfferedOption, selected: boolean) => {
    if (offeredOption.option_type !== 'product_catalogue' && selected) return null
    if (offeredOption.logo?.url) return <img className="iconSVG" src={ offeredOption.logo.url } />
    return <svg className="iconSVG" version="1.1" x="0px" y="0px" viewBox="0 0 512 512" dangerouslySetInnerHTML={{ __html: '<g id="_x37_22-_add__x2C__add_more__x2C__plus__x2C_"><g><line style="fill:none;stroke:#000000;stroke-width:13.4167;stroke-linecap:round;stroke-linejoin:round;stroke-miterlimit:2.6131;" x1="256.486" x2="256.486" y1="26.814" y2="486.139"/><line style="fill:none;stroke:#000000;stroke-width:13.4167;stroke-linecap:round;stroke-linejoin:round;stroke-miterlimit:2.6131;" x1="486.238" x2="26.711" y1="256.477" y2="256.477"/></g></g><g id="Layer_1"/>' }}></svg>
  }, [])

  const renderOfferedOptionOfferedServiceIcon = React.useCallback((offeredOptionOfferedService: OfferedOptionOfferedService) => {
    const imageUrl = offeredOptionOfferedService.offered_service.description_image.url
    if (imageUrl) return <img className="iconSVG" src={ imageUrl } />
    return <svg className="iconSVG" version="1.1" x="0px" y="0px" viewBox="0 0 512 512" dangerouslySetInnerHTML={{ __html: '<g id="_x37_22-_add__x2C__add_more__x2C__plus__x2C_"><g><line style="fill:none;stroke:#000000;stroke-width:13.4167;stroke-linecap:round;stroke-linejoin:round;stroke-miterlimit:2.6131;" x1="256.486" x2="256.486" y1="26.814" y2="486.139"/><line style="fill:none;stroke:#000000;stroke-width:13.4167;stroke-linecap:round;stroke-linejoin:round;stroke-miterlimit:2.6131;" x1="486.238" x2="26.711" y1="256.477" y2="256.477"/></g></g><g id="Layer_1"/>' }}></svg>
  }, [])

  const renderOfferedOptionRange = React.useCallback((offeredOption: OfferedOption & { option_type: 'given_duration' | 'given_quantity' }, selectedOption: SelectedOption) => {
    const currentValueDurationStr = offeredOption.option_type === 'given_duration' ? '+' + formatDurationFromSecs(selectedOption.selected_value) : selectedOption.selected_value.toString()

    return <RangeBox>
      <RangeDuration style={ currentValueDurationStr.length > 5 ? { fontSize: currentValueDurationStr.length > 7 ? '0.6em' : '0.8em' } : undefined }>{ currentValueDurationStr }</RangeDuration>
      { offeredOption.value_min !== offeredOption.value_max && <InputRangeComponent
        start={ selectedOption.selected_value }
        range={{ min: offeredOption.value_min, max: offeredOption.value_max }}
        step={ offeredOption.value_step }
        onChange={ val => handleSliderChange(selectedOption, val) }
        className="noUi--light noUi--S" /> }
    </RangeBox>
  }, [handleSliderChange])

  const renderOfferedOptionOfferedServiceRange = React.useCallback(({ offered_service }: OfferedOptionOfferedService, selectedOption: SelectedOption) => {
    return <RangeBox>
      <RangeDuration>{ selectedOption.selected_value }</RangeDuration>
      { offered_service.quantity_min !== offered_service.quantity_max && <InputRangeComponent
        start={ selectedOption.selected_value }
        range={{ min: offered_service.quantity_min, max: offered_service.quantity_max }}
        step={ offered_service.quantity_step }
        onChange={ val => handleSliderChange(selectedOption, val) }
        className="noUi--light noUi--S" /> }
    </RangeBox>
  }, [handleSliderChange])

  const openedCatalogue: OfferedOption & { option_type: 'product_catalogue' } = os.offered_options.find(_ => _.option_type === 'product_catalogue' && _.id === currentOpenCatalogueId) as any

  return <>
    { os.require_at_least_one_selected_option && i18nScoped.t('require_at_least_one_selected_option') }

    { /* options and catalogue "root" */ }
    <SelectableIcons>
      { os.offered_options.map((offeredOption, index) => {
        const selectedOption = selectedOptions.find(_ => _.offered_option_id === offeredOption.id)
        const selected = !!selectedOption || offeredOption.id === currentOpenCatalogueId
        if (!store.isOfferedOptionVisible(offeredOption)) return null

        return <SelectableItem
          key={ offeredOption.id }
          onMouseEnter={ () => setFocusedDescription(offeredOption.description) }
          onMouseLeave={ () => setFocusedDescription(null) }>

          <input type="checkbox" id={ `offeredOptions${index}` } onChange={ () => onOfferedOptionClicked(offeredOption, !selected) } checked={ selected } />
          <label htmlFor={ `offeredOptions${index}` }>
            <SelectableIconContainer $selected={ selected }>{ renderOfferedOptionIcon(offeredOption, selected) }</SelectableIconContainer>
            <SelectableIconText $selected={ selected }>{ offeredOption.name }</SelectableIconText>
          </label>

          { selectedOption && offeredOption.option_type !== 'product_catalogue' && renderOfferedOptionRange(offeredOption, selectedOption) }
        </SelectableItem>
      }) }
    </SelectableIcons>

    { /* deployed catalogue's suboptions */ }
    { openedCatalogue && <SelectableIcons>
      { openedCatalogue.offered_option_offered_services.map((offeredOptionOfferedService, index) => {
        const selectedOption = selectedOptions.find(_ => _.offered_option_id === currentOpenCatalogueId && _.offered_service_id === offeredOptionOfferedService.offered_service.id)

        return <SelectableItem key={ `cat${offeredOptionOfferedService.id}` }
          onMouseEnter={ () => setFocusedDescription(offeredOptionOfferedService.description) }
          onMouseLeave={ () => setFocusedDescription(null) }>
          <input
            type="checkbox"
            id={ `catalog${index}` }
            value={ offeredOptionOfferedService.offered_service.name }
            onChange={ () => onOfferedOptionOfferedServiceClicked(openedCatalogue, offeredOptionOfferedService, selectedOption) }
            checked={ !!selectedOption } />

          <label htmlFor={ `catalog${index}` }>
            <SelectableIconContainer $selected={ !!selectedOption }>{ !selectedOption && renderOfferedOptionOfferedServiceIcon(offeredOptionOfferedService) }</SelectableIconContainer>
            <SelectableIconText $selected={ !!selectedOption }>{ offeredOptionOfferedService.offered_service.name }</SelectableIconText>
          </label>

          { selectedOption && renderOfferedOptionOfferedServiceRange(offeredOptionOfferedService, selectedOption) }
        </SelectableItem>
      }) }
    </SelectableIcons> }

    { focusedDescription && <SelectableIconDescription>{ focusedDescription }</SelectableIconDescription> }
  </>
}
